import { TellescopeModelFromName } from "../mappings/";
import { FormResponseGroup } from "./types";

/**
 * Turns every instance of a `Date` type into `Date | string` deeply within any object
 */
type DeeplyConvertDateToDateOrString<T extends object> = {
  [K in keyof T]: T[K] extends Date
    ? Date | string
    : T[K] extends object
    ? DeeplyConvertDateToDateOrString<T[K]>
    : T[K];
};

/**
 * Groups form responses by their form ID and orders them by submission time.
 *
 * This utility takes an array of form responses and:
 * 1. Groups responses by their formId
 * 2. Sorts responses within each group by submittedAt (falls back to updatedAt if not available) (newest first)
 * 3. Sorts the form groups by their newest response (newest forms first)
 *
 * @param formResponses - Array of form response objects to be grouped and sorted
 * @returns An object containing an array of FormData objects, each representing
 *          a form with its responses ordered by submission time (newest first).
 *          The forms array itself is ordered by the newest response in each form.
 *          If submittedAt is not available, updatedAt is used as a fallback.
 *
 * @example
 * ```typescript
 * const responses = [
 *   { id: '1', formId: 'form-a', submittedAt: new Date('2025-01-01') },
 *   { id: '2', formId: 'form-b', submittedAt: new Date('2025-01-03') },
 *   { id: '3', formId: 'form-a', submittedAt: new Date('2025-01-02') },
 * ];
 *
 * const result = combineFormResponsesByForm(responses);
 * // result.forms = [
 * //   { formId: 'form-b', responses: [{ id: '2', ... }] },
 * //   { formId: 'form-a', responses: [{ id: '3', ... }, { id: '1', ... }] }
 * // ]
 * ```
 */
export const combineFormResponsesByForm = (
  formResponses: TellescopeModelFromName<"form_responses">[]
): {
  forms: FormResponseGroup[];
} => {
  const formsMap = formResponses.reduce<
    Record<
      string,
      DeeplyConvertDateToDateOrString<{
        newestResponse: Date | string;
        responses: TellescopeModelFromName<"form_responses">[];
      }>
    >
  >((acc, curr) => {
    const currentEntry = acc[curr.formId] ?? {
      newestResponse: new Date(0),
      responses: [],
    };
    const newResponses = [...currentEntry.responses, curr].sort((a, b) => {
      const aDate = a.submittedAt ?? a.updatedAt;
      const bDate = b.submittedAt ?? b.updatedAt;
      return (bDate ? new Date(bDate).getTime() : 0) - (aDate ? new Date(aDate).getTime() : 0);
    });
    acc[curr.formId] = {
      responses: newResponses,
      newestResponse: newResponses[0]?.submittedAt ?? newResponses[0]?.updatedAt ?? new Date(0),
    };
    return acc;
  }, {});

  const formsEntries = Object.entries(formsMap);

  const orderedFormsEntries = formsEntries.sort(([, { newestResponse: a }], [, { newestResponse: b }]) => {
    const aDate = new Date(a);
    const bDate = new Date(b);
    if (aDate.getTime() < bDate.getTime()) {
      return 1;
    } else if (bDate.getTime() < aDate.getTime()) {
      return -1;
    }
    return 0;
  });

  return {
    forms: orderedFormsEntries.map(([formId, { responses }]) => ({
      formId,
      formTitle: responses[0]?.formTitle ?? "",
      responses: responses as TellescopeModelFromName<"form_responses">[],
    })),
  };
};
